import { useEffect, useState, useCallback } from "react";
import api, { apiError, fileUrl, formatBytes, formatDuration, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Download, Check, Trash2, Loader2, Link2, Copy, QrCode, ExternalLink, RefreshCw, Ban, Pencil, ShieldCheck, ShieldAlert, Lock, Share2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const SUGGESTED = "Your event highlights are ready! We hope you enjoy reliving the memories. Use the private link below to view and download your reel.";

export default function ReviewSharePanel({ event, reload }) {
  const [outputs, setOutputs] = useState([]);
  const [gl, setGl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [renaming, setRenaming] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const [pwInput, setPwInput] = useState("");
  const [qrOpen, setQrOpen] = useState(false);
  const [sharing, setSharing] = useState(event.sharing_status || "private");
  const [pendingDownload, setPendingDownload] = useState(null);

  const origin = window.location.origin;
  const guestUrl = gl ? `${origin}/g/${gl.token}` : "";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, g] = await Promise.all([api.get(`/events/${event.id}/renders`), api.get(`/events/${event.id}/guest-link`)]);
      setOutputs(r.data.outputs);
      setGl(g.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [event.id]);

  useEffect(() => { load(); }, [load]);

  const approve = async (o) => { try { const { data } = await api.patch(`/outputs/${o.id}`, { approved: !o.approved }); setOutputs((os) => os.map((x) => x.id === o.id ? data : x)); } catch (e) { toast.error(apiError(e)); } };
  const rename = async (id) => { try { const { data } = await api.patch(`/outputs/${id}`, { name: renameVal }); setOutputs((os) => os.map((x) => x.id === id ? data : x)); setRenaming(null); } catch (e) { toast.error(apiError(e)); } };
  const del = async (id) => { if (!window.confirm("Delete this output?")) return; try { await api.delete(`/outputs/${id}`); load(); } catch (e) { toast.error(apiError(e)); } };
  const doDownload = (o) => { window.open(fileUrl(`/outputs/${o.id}/file`) + "&download=1", "_blank"); };
  const download = (o) => { if (sharing !== "social_ok") { setPendingDownload(o); } else { doDownload(o); } };
  const setSharingStatus = async (v) => {
    setSharing(v);
    try { await api.put(`/events/${event.id}`, { sharing_status: v }); reload(); toast.success(v === "social_ok" ? "Marked: social sharing allowed" : "Marked: private delivery only"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const createLink = async () => { try { const { data } = await api.post(`/events/${event.id}/guest-link`, { message: SUGGESTED }); setGl(data); reload(); toast.success("Guest link created"); } catch (e) { toast.error(apiError(e)); } };
  const updateLink = async (fields) => { try { const { data } = await api.put(`/events/${event.id}/guest-link`, fields); setGl(data); reload(); } catch (e) { toast.error(apiError(e)); } };
  const regenerate = async () => { if (!window.confirm("Regenerate link? The old link will stop working.")) return; try { const { data } = await api.post(`/events/${event.id}/guest-link/regenerate`); setGl(data); toast.success("New link generated"); } catch (e) { toast.error(apiError(e)); } };
  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copied"); };

  const toggleGuestOutput = (id) => {
    const cur = gl.output_ids || [];
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    updateLink({ output_ids: next });
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-gold animate-spin" /></div>;

  return (
    <div className="space-y-8">
      <div>
        <h2 className="font-display text-3xl text-ivory">Review & Share</h2>
        <p className="text-muted-foreground text-sm mt-1">Review your outputs, approve the best ones, then create a private guest link. Nothing is sent automatically.</p>
      </div>

      {/* Sharing consent */}
      <div data-testid="sharing-consent" className="bg-navy-card border border-navy-border rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="w-5 h-5 text-gold" />
          <h3 className="text-ivory font-medium">Photo consent & sharing</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-4">Record what the client agreed to. This controls a friendly warning before anyone downloads a social-ready pack.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <button data-testid="sharing-status-private" onClick={() => setSharingStatus("private")}
            className={`rounded-xl border p-4 text-left transition-colors ${sharing !== "social_ok" ? "border-gold bg-gold/10" : "border-navy-border hover:border-gold/50"}`}>
            <div className="flex items-center gap-2 text-ivory text-sm font-medium"><Lock className="w-4 h-4" /> Private delivery only</div>
            <p className="text-[11px] text-muted-foreground mt-1">Client has not consented to social media. Warn before downloading social packs.</p>
          </button>
          <button data-testid="sharing-status-social" onClick={() => setSharingStatus("social_ok")}
            className={`rounded-xl border p-4 text-left transition-colors ${sharing === "social_ok" ? "border-emerald-500 bg-emerald-500/10" : "border-navy-border hover:border-gold/50"}`}>
            <div className="flex items-center gap-2 text-ivory text-sm font-medium"><Share2 className="w-4 h-4" /> Social sharing allowed</div>
            <p className="text-[11px] text-muted-foreground mt-1">Client is happy for highlights to be posted publicly.</p>
          </button>
        </div>
      </div>

      {/* Outputs */}
      {outputs.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-navy-border rounded-2xl text-muted-foreground">No finished outputs yet. Create a reel first.</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {outputs.map((o) => (
            <div key={o.id} data-testid={`output-${o.id}`} className="bg-navy-card border border-navy-border rounded-2xl overflow-hidden">
              <div className="bg-navy-deep flex items-center justify-center" style={{ aspectRatio: `${o.width}/${o.height}`, maxHeight: 360 }}>
                {o.type === "gif" ? (
                  <img src={fileUrl(`/outputs/${o.id}/file`)} alt={o.name} className="w-full h-full object-contain" />
                ) : (
                  <video data-testid={`output-video-${o.id}`} src={fileUrl(`/outputs/${o.id}/file`)} poster={fileUrl(`/outputs/${o.id}/poster`)} controls className="w-full h-full object-contain" />
                )}
              </div>
              <div className="p-4 space-y-3">
                {renaming === o.id ? (
                  <div className="flex gap-2">
                    <Input data-testid={`rename-input-${o.id}`} value={renameVal} onChange={(e) => setRenameVal(e.target.value)} className="h-8 bg-navy-deep border-navy-border" />
                    <Button data-testid={`rename-save-${o.id}`} size="sm" onClick={() => rename(o.id)} className="bg-gold text-navy h-8">Save</Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-ivory font-medium truncate">{o.name}</span>
                    <button data-testid={`rename-${o.id}`} onClick={() => { setRenaming(o.id); setRenameVal(o.name); }} className="text-muted-foreground hover:text-ivory shrink-0"><Pencil className="w-3.5 h-3.5" /></button>
                  </div>
                )}
                <div className="flex flex-wrap gap-2 text-[11px] text-muted-foreground">
                  <Badge variant="outline" className="border-navy-border text-champagne">{o.type.toUpperCase()}</Badge>
                  <span>{o.width}×{o.height}</span><span>· {formatDuration(o.duration)}</span><span>· {formatBytes(o.size)}</span><span>· v{o.version}</span>
                </div>
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <Button data-testid={`approve-${o.id}`} size="sm" variant={o.approved ? "default" : "outline"} onClick={() => approve(o)}
                    className={o.approved ? "bg-emerald-600 hover:bg-emerald-700 h-8" : "border-navy-border h-8"}>
                    <Check className="w-3.5 h-3.5 mr-1" /> {o.approved ? "Approved" : "Approve"}
                  </Button>
                  <Button data-testid={`download-${o.id}`} size="sm" variant="outline" onClick={() => download(o)} className="border-navy-border h-8"><Download className="w-3.5 h-3.5 mr-1" /> Download</Button>
                  <button data-testid={`delete-output-${o.id}`} onClick={() => del(o.id)} className="text-muted-foreground hover:text-red-400 ml-auto"><Trash2 className="w-4 h-4" /></button>
                </div>
                {gl && (
                  <label className="flex items-center gap-2 text-xs text-champagne pt-1 border-t border-navy-border mt-2 cursor-pointer">
                    <Checkbox data-testid={`guest-include-${o.id}`} checked={(gl.output_ids || []).includes(o.id)} onCheckedChange={() => toggleGuestOutput(o.id)} />
                    Show on guest page
                  </label>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Guest link */}
      <div className="bg-navy-card border border-navy-border rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Link2 className="w-5 h-5 text-gold" />
          <h3 className="font-display text-2xl text-ivory">Guest Link</h3>
        </div>

        {!gl ? (
          <div className="text-center py-6">
            <p className="text-muted-foreground text-sm mb-4">Create a private link for your client. They won't need an account.</p>
            <Button data-testid="create-guest-link-button" onClick={createLink} className="rounded-full bg-gold hover:bg-gold-dark text-navy font-semibold px-6">
              <Link2 className="w-4 h-4 mr-1.5" /> Create Guest Link
            </Button>
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-champagne">Private link</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input data-testid="guest-link-url" readOnly value={guestUrl} className="bg-navy-deep border-navy-border font-mono text-xs" />
                  <Button data-testid="copy-link-button" size="icon" variant="outline" onClick={() => copy(guestUrl)} className="border-navy-border shrink-0"><Copy className="w-4 h-4" /></Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Button data-testid="open-guest-page-button" size="sm" variant="outline" onClick={() => window.open(guestUrl, "_blank")} className="border-navy-border"><ExternalLink className="w-3.5 h-3.5 mr-1" /> Open</Button>
                  <Button data-testid="qr-button" size="sm" variant="outline" onClick={() => setQrOpen((v) => !v)} className="border-navy-border"><QrCode className="w-3.5 h-3.5 mr-1" /> QR code</Button>
                  <Button data-testid="regenerate-link-button" size="sm" variant="outline" onClick={regenerate} className="border-navy-border"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Regenerate</Button>
                </div>
                {qrOpen && <img data-testid="qr-image" alt="QR" src={fileUrl(`/events/${event.id}/guest-link/qr`) + `&base_url=${encodeURIComponent(origin)}`} className="mt-3 w-40 h-40 bg-white rounded-lg p-2" />}
              </div>

              <div>
                <Label className="text-xs text-champagne">Suggested message</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input readOnly value={gl.message || SUGGESTED} className="bg-navy-deep border-navy-border text-xs" />
                  <Button data-testid="copy-message-button" size="icon" variant="outline" onClick={() => copy(gl.message || SUGGESTED)} className="border-navy-border shrink-0"><Copy className="w-4 h-4" /></Button>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div><Label className="text-sm text-champagne">Allow downloads</Label><p className="text-xs text-muted-foreground">Let guests download the files</p></div>
                <Switch data-testid="downloads-switch" checked={gl.downloads_enabled} onCheckedChange={(v) => updateLink({ downloads_enabled: v })} />
              </div>
              <div className="flex items-center justify-between">
                <div><Label className="text-sm text-champagne">Disable link</Label><p className="text-xs text-muted-foreground">Instantly turn off access</p></div>
                <Switch data-testid="disable-switch" checked={gl.disabled} onCheckedChange={(v) => updateLink({ disabled: v })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-champagne">Expiry date</Label>
                <Input data-testid="expiry-input" type="date" value={gl.expiry ? gl.expiry.slice(0, 10) : ""} onChange={(e) => updateLink({ expiry: e.target.value ? new Date(e.target.value).toISOString() : null })} className="bg-navy-deep border-navy-border" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-champagne flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Password protection {gl.has_password && <Badge variant="outline" className="border-gold/40 text-gold text-[10px]">On</Badge>}</Label>
                <div className="flex gap-2">
                  <Input data-testid="password-input" type="text" placeholder={gl.has_password ? "Enter new password" : "Set a password"} value={pwInput} onChange={(e) => setPwInput(e.target.value)} className="bg-navy-deep border-navy-border" />
                  <Button data-testid="set-password-button" size="sm" variant="outline" onClick={() => { updateLink({ password: pwInput }); setPwInput(""); }} className="border-navy-border">Set</Button>
                  {gl.has_password && <Button data-testid="remove-password-button" size="sm" variant="outline" onClick={() => updateLink({ password: "" })} className="border-navy-border text-red-400"><Ban className="w-3.5 h-3.5" /></Button>}
                </div>
              </div>
              {gl.expiry && <p className="text-xs text-muted-foreground">Expires {formatDate(gl.expiry)}</p>}
            </div>
          </div>
        )}
      </div>
      {/* Download consent warning */}
      <Dialog open={!!pendingDownload} onOpenChange={(o) => !o && setPendingDownload(null)}>
        <DialogContent data-testid="download-warning-dialog" className="bg-navy-card border-navy-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-ivory flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-amber-400" /> Private delivery only</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This event is marked <span className="text-ivory font-medium">Private delivery only</span> — the client has not consented to social media sharing.
            Please only download this for private delivery to the client, not for public posting.
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPendingDownload(null)} className="border-navy-border">Cancel</Button>
            <Button data-testid="confirm-download-button" onClick={() => { const o = pendingDownload; setPendingDownload(null); doDownload(o); }}
              className="rounded-full bg-gold hover:bg-gold-dark text-navy font-semibold">
              <Download className="w-4 h-4 mr-1.5" /> Download for private delivery
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
